import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/apiService";
import analyticsService from "../services/analyticsService";
import { useFeaturedCars } from "../hooks/useFeaturedCars";
import "./login.css";
import logo from "./assets/trustauto-logo.png";
import carImage1 from "./assets/car_image_1.svg";
import carImage2 from "./assets/car_image_2.svg";
import { Loading } from "./components/Loading";


const Login = () => {
  const [error, setError] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoSliding, setIsAutoSliding] = useState(true);
  const [isLoading, setIsLoading] = useState(false); // New state for loading
  const { images } = useFeaturedCars();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      navigate("/dealerhome", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!isAutoSliding) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isAutoSliding, images.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const password = e.target.password.value;

    setIsLoading(true); // Set loading to true when API call starts
    try {
      const loginResponse = await apiService.login({ email, password });
      const { token, user } = loginResponse.data;

      localStorage.setItem("authToken", token);
      localStorage.setItem("uid", user.uid);
      localStorage.setItem("email", user.email);
      localStorage.setItem("orgId", user.organization);
      localStorage.setItem("status", user.status || 'NA');

      if (user.name) localStorage.setItem("userName", user.name);
      if (user.profileImageUrl)
        localStorage.setItem("profileImageUrl", user.profileImageUrl);

      analyticsService.setUser({
        uid: user.uid,
        email: user.email,
        displayName: user.name || user.email,
        roles: user.roles || ['orguserdealer'],
        organization: user.organization,
        dealerId: user.uid
      });

      window.dispatchEvent(new CustomEvent("userDataUpdated"));

      console.log("🔍 Login - Full user data from API:", loginResponse.data);
      switch (loginResponse.data.onboarding) {
        case "dealer_info":
          localStorage.setItem("onboardingStatus", "/dealer/details");
          navigate("/dealer/details");
          break;
        case "subscription":
          localStorage.setItem("onboardingStatus", "/dealer/subscription");
          navigate("/dealer/subscription");
          break;
        case "upload_docs":
          localStorage.setItem("onboardingStatus", "/dealer/upload");
          navigate("/dealer/upload");
          break;
        case "contract":
          localStorage.setItem("onboardingStatus", "/dealer/contract");
          navigate("/dealer/contract");
          break;
        case "pending_approval":
          alert("Your account is pending approval. Please wait for confirmation.");
          apiService.logout();
          break;
        case "otp_login":
          alert("Please complete OTP login.");
          apiService.logout();
          break;
        default:
          navigate("/dealerHome");
      }
    } catch (err) {
      console.error("Error during login:", err);
      setError(
        err.response?.data?.error?.message ||
          "Login failed. Please check your credentials and try again."
      );
    } finally {
      setIsLoading(false); // Set loading to false when API call completes
    }
  };

  const handlePrevImage = () => {
    setIsAutoSliding(false);
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setTimeout(() => setIsAutoSliding(true), 5000);
  };

  const handleNextImage = () => {
    setIsAutoSliding(false);
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
    setTimeout(() => setIsAutoSliding(true), 5000);
  };

  const handleDotClick = (index) => {
    setIsAutoSliding(false);
    setCurrentImageIndex(index);
    setTimeout(() => setIsAutoSliding(true), 5000);
  };

  return (
    <div className="wrap_login">
      <div className="login">
        <div className="container">
          <div className="left-part">
            <div className="left-part-content">
              <img src={logo} alt="Trust Auto Logo" className="login-logo" />
              <div className="welcome-msg">
                <h1>Welcome Back!</h1>
                <p>Sign in to access Trust Auction Platform</p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="inputs">
                  <label className="email">Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="example@trustauto.in"
                    required
                  />

                  <label className="pass">Password</label>
                  <input
                    type="password"
                    name="password"
                    placeholder="********"
                    required
                  />

                  <label className="captcha">Captcha</label>
                  <div className="captcha-input-box">
                    <input
                      type="checkbox"
                      name="human"
                      className="captcha-input"
                    />
                    <label htmlFor="human">Are you a Human?</label>
                  </div>
                </div>

                <div className="auth-msg">
                  <input type="checkbox" name="signedIn" />
                  <label htmlFor="signedIn">Keep me signed in</label>
                  <a href="#" className="forget-pass">
                    Forgot Password?
                  </a>
                </div>

                {error && <p className="error-message">⚠️ {error}</p>}

                <div className="buttons">
                  {isLoading ? (
                    <Loading /> // Render Loading component during API call
                  ) : (
                    <button className="signIn" type="submit">
                      SIGN IN
                    </button>
                  )}
                </div>
              </form>

              <div className="reg-or-log-msg">
                <p>
                  Don't have an account?
                  <span
                    className="register-link"
                    onClick={() => navigate("/dealer/register")}
                  >
                    Register
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="right-part">
            <div className="right-part-content">
              <div className="images">
                <div className="carousel">
                  <div className="dots">
                    {images.map((_, index) => (
                      <span
                        key={index}
                        className={`dot ${currentImageIndex === index ? "active" : ""}`}
                        onClick={() => handleDotClick(index)}
                      ></span>
                    ))}
                  </div>
                  <div className="carousel-track" style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}>
                    {images.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt="Car"
                        className="carImage"
                        onError={(e) => {
                          // Fallback to default images if featured image fails to load
                          const defaultImages = [carImage1, carImage2, carImage2];
                          e.target.src = defaultImages[index % defaultImages.length];
                        }}
                      />
                    ))}
                  </div>
                  <div className="carousel-controls">
                    <button
                      className="carousel-button prev"
                      onClick={handlePrevImage}
                    >
                      <span>&lt;</span>
                    </button>
                    <button
                      className="carousel-button next"
                      onClick={handleNextImage}
                    >
                      <span>&gt;</span>
                    </button>
                  </div>
                  <div className="image-content">
                    <h2>Cars In Auction Today</h2>
                    <p>Bid Bold. Drive Trusted</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;